import type { Json } from "@/integrations/supabase/types";
import {
  appendContributionBatchArgs, readContributionBatchArgs,
  updateContributionBatchDetailsArgs, updateContributionBatchMemberArgs,
  type AppendContributionBatchRequest, type ChangeContributionBatchDateRequest,
  type ContributionBatchCompareAndSwap, type ContributionBatchReceipt,
  type ContributionBatchSnapshot, type ContributionBatchMemberUpdateResult,
  type ContributionBatchHeader, type ContributionBatchStatus,
  type UpdateContributionBatchDetailsRequest, type UpdateContributionBatchMemberRequest,
} from "./coherent-contract";
import type { SavedBatchCorePort, SavedBatchMemberReadModel, SavedBatchReadRequest, SavedBatchReadSnapshot } from "./core-port";

type RpcResult = { data: Json | null; error: { message: string; code?: string } | null };
/** Structural contract used by the authenticated REST adapter and isolated
 * tests. This shape alone does not qualify a server schema or generated SDK. */
export interface CoherentSavedBatchTransport {
  rpc(name: "finance_read_contribution_batch", args: SavedBatchReadRequest): PromiseLike<RpcResult>;
  rpc(name: "finance_change_contribution_batch_date", args: ChangeContributionBatchDateRequest): PromiseLike<RpcResult>;
  rpc(name: "finance_reopen_contribution_batch", args: ContributionBatchCompareAndSwap): PromiseLike<RpcResult>;
  rpc(name: "finance_append_contribution_batch", args: ReturnType<typeof appendContributionBatchArgs>): PromiseLike<RpcResult>;
  rpc(name: "finance_update_contribution_batch_details", args: ReturnType<typeof updateContributionBatchDetailsArgs>): PromiseLike<RpcResult>;
  rpc(name: "finance_update_contribution_batch_member", args: ReturnType<typeof updateContributionBatchMemberArgs>): PromiseLike<RpcResult>;
}

/** Separate read transport for resolving a report's opaque legacy batch link. */
export interface CoherentSavedBatchListTransport {
  rpc(name: "finance_list_contribution_batches", args: { p_organization_id: string }): PromiseLike<RpcResult>;
}

export class CoherentSavedBatchError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = "CoherentSavedBatchError";
  }
}

/** Constructed only by the local authenticated transport before HTTP dispatch.
 * A JSON error field or an error code alone cannot establish this proof. */
export class SavedBatchPreDispatchError extends CoherentSavedBatchError {
  constructor(message: string, code?: string) {
    super(message, code);
    this.name = "SavedBatchPreDispatchError";
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REVISION = /^[0-9a-f]{64}$/;
const statuses = ["open", "closed", "deleted", "review_required"];
const routes = ["admin_ownership_review", "connected_source", "individual_finance_lifecycle"];
function requireValid(condition: unknown): asserts condition {
  if (!condition) throw new CoherentSavedBatchError("Invalid coherent saved-batch contract");
}
function object(value: unknown): Record<string, unknown> {
  requireValid(value !== null && typeof value === "object" && !Array.isArray(value));
  return value as Record<string, unknown>;
}
const uuid = (value: unknown): value is string => typeof value === "string" && UUID.test(value);
const nullableString = (value: unknown) => value === null || typeof value === "string";
const nullableUuid = (value: unknown) => value === null || uuid(value);
const count = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");
function ids(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(uuid) && new Set(value).size === value.length;
}
function date(value: unknown): boolean {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
const timestamp = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value));
const nullableTimestamp = (value: unknown) => value === null || timestamp(value);
function cas(request: ContributionBatchCompareAndSwap) {
  requireValid(uuid(request.p_organization_id) && uuid(request.p_batch_id) && uuid(request.p_operation_id)
    && count(request.p_expected_revision) && request.p_expected_revision >= 1
    && ids(request.p_complete_member_ids) && typeof request.p_reason === "string"
    && request.p_reason.replace(/^ +| +$/g, "").length > 0 && Array.from(request.p_reason).length <= 2000);
}
function casArgs(request: ContributionBatchCompareAndSwap): ContributionBatchCompareAndSwap {
  cas(request);
  return {
    p_organization_id: request.p_organization_id, p_batch_id: request.p_batch_id,
    p_expected_revision: request.p_expected_revision, p_complete_member_ids: [...request.p_complete_member_ids],
    p_reason: request.p_reason, p_operation_id: request.p_operation_id,
  };
}

/** Validate the raw DTO before projecting any edit values or authority guidance. */
export function projectCoherentSavedBatch(value: Json | null, request: SavedBatchReadRequest): SavedBatchReadSnapshot {
  const raw = object(value);
  const header = object(raw.header);
  requireValid(uuid(request.p_organization_id) && uuid(request.p_batch_id)
    && header.organization_id === request.p_organization_id && header.id === request.p_batch_id
    && typeof header.legacy_batch_id === "string" && header.legacy_batch_id.length > 0
    && count(header.revision) && header.revision >= 1 && statuses.includes(String(header.status))
    && typeof header.managed === "boolean" && (header.batch_date === null || date(header.batch_date))
    && nullableString(header.event_name) && timestamp(header.created_at) && timestamp(header.updated_at)
    && nullableTimestamp(header.closed_at) && nullableTimestamp(header.deleted_at)
    && raw.snapshot_semantics === "statement" && ids(raw.complete_member_ids) && Array.isArray(raw.members));
  const completeIds = raw.complete_member_ids as string[];
  requireValid(completeIds.every((id, index) => index === 0 || completeIds[index - 1] < id));
  const eligibility = object(raw.eligibility);
  requireValid(typeof eligibility.eligible === "boolean" && strings(eligibility.header_blockers)
    && Array.isArray(eligibility.member_blockers));
  for (const item of eligibility.member_blockers as unknown[]) {
    const blocker = object(item);
    requireValid(uuid(blocker.contribution_id) && completeIds.includes(blocker.contribution_id)
      && strings(blocker.blockers) && routes.includes(String(blocker.correction_route)));
  }
  requireValid(raw.members.length === completeIds.length);
  for (const [index, item] of raw.members.entries()) {
    const member = object(item);
    requireValid(member.id === completeIds[index] && nullableUuid(member.person_id) && date(member.date)
      && (member.batch_date === null || date(member.batch_date)) && nullableTimestamp(member.deleted_at)
      && typeof member.amount === "number" && Number.isFinite(member.amount)
      && typeof member.method === "string" && nullableString(member.check_number) && nullableString(member.notes)
      && nullableUuid(member.campus_id) && typeof member.status === "string"
      && typeof member.revision === "string" && REVISION.test(member.revision)
      && (member.auto_post_to_gl === null || typeof member.auto_post_to_gl === "boolean")
      && count(member.allocation_count) && count(member.organization_allocation_count)
      && member.organization_allocation_count <= member.allocation_count
      && member.has_allocations === (member.allocation_count > 0));
    const edit = object(member.edit_eligibility);
    const fields = object(edit.field_restrictions);
    requireValid(typeof edit.eligible === "boolean" && strings(edit.blockers)
      && (edit.correction_route === null || routes.includes(String(edit.correction_route)))
      && typeof edit.amount_change_blocked_by_allocations === "boolean"
      && edit.value_dependent_checks_required === true && fields.value_dependent_checks_required === true
      && edit.amount_change_blocked_by_allocations === fields.amount_change_requires_allocation_replacement
      && strings(fields.allowed_methods) && fields.allowed_methods.join(",") === "cash,check,other");
    for (const key of ["amount_change_requires_allocation_replacement", "current_person_valid", "current_campus_valid",
      "current_method_valid", "current_check_number_valid", "current_status_valid", "non_notes_change_requires_source_workflow"]) {
      requireValid(typeof fields[key] === "boolean");
    }
    requireValid(edit.eligible !== true || edit.blockers.length === 0);
  }
  // The checks above cover every consumed field; no generated RPC metadata cast.
  const snapshot = structuredClone(value) as unknown as ContributionBatchSnapshot;
  const memberReadModels: SavedBatchMemberReadModel[] = snapshot.members.map((member) => ({
    organizationId: snapshot.header.organization_id, batchId: snapshot.header.id,
    legacyBatchId: snapshot.header.legacy_batch_id, batchRevision: snapshot.header.revision,
    memberId: member.id, financialRevision: member.revision, batchConsistent: true,
    canEdit: member.edit_eligibility.eligible, blockers: [...member.edit_eligibility.blockers],
    hasAllocations: member.has_allocations, fieldRestrictions: structuredClone(member.edit_eligibility.field_restrictions),
    values: { personId: member.person_id, amount: member.amount, method: member.method,
      checkNumber: member.check_number, notes: member.notes, campusId: member.campus_id },
  }));
  return { ...snapshot, completeMemberIds: [...snapshot.complete_member_ids], memberReadModels };
}

function receipt(value: unknown, request: ContributionBatchCompareAndSwap, action: "changeDate" | "reopen" | "append" | "updateDetails" | "updateMember", createdCount = 0): ContributionBatchReceipt {
  const raw = object(value);
  requireValid(raw.batch_id === request.p_batch_id && raw.revision === request.p_expected_revision + 1
    && statuses.includes(String(raw.status)) && ids(raw.contribution_ids));
  const memberIds = raw.contribution_ids as string[];
  requireValid(request.p_complete_member_ids.every((id) => memberIds.includes(id))
    && memberIds.length === request.p_complete_member_ids.length + createdCount);
  if (action === "append") requireValid(raw.status === "open" && raw.created_count === createdCount);
  else {
    requireValid((action === "reopen" ? raw.status === "open" : ["open", "closed"].includes(String(raw.status)))
      && raw.updated_count === (action === "changeDate" ? request.p_complete_member_ids.length : action === "reopen" ? 0 : 1));
  }
  for (const key of ["created_count", "updated_count", "deleted_count"]) {
    requireValid(raw[key] === undefined || count(raw[key]));
  }
  requireValid(raw.deleted_count === undefined || raw.deleted_count === 0);
  if (action === "append") requireValid(raw.updated_count === undefined || raw.updated_count === 0);
  else requireValid(raw.created_count === undefined || raw.created_count === 0);
  return structuredClone(raw) as unknown as ContributionBatchReceipt;
}
async function data(result: PromiseLike<RpcResult>): Promise<Json | null> {
  const response = await result;
  requireValid(response && typeof response === "object" && "data" in response && "error" in response);
  if (response.error !== null) {
    requireValid(typeof response.error.message === "string"
      && (response.error.code === undefined || typeof response.error.code === "string"));
    if (response.error instanceof SavedBatchPreDispatchError) throw response.error;
    throw new CoherentSavedBatchError(response.error.message, response.error.code);
  }
  return response.data;
}

function checkedString(value: unknown): string {
  requireValid(typeof value === "string");
  return value;
}
function checkedNullableString(value: unknown): string | null {
  if (value === null) return null;
  requireValid(typeof value === "string");
  return value;
}
function checkedStatus(value: unknown): ContributionBatchStatus {
  requireValid(value === "open" || value === "closed" || value === "deleted" || value === "review_required");
  return value;
}
function listedHeader(value: unknown, organizationId: string): ContributionBatchHeader {
  const header = object(value);
  requireValid(uuid(header.id) && header.organization_id === organizationId
    && typeof header.legacy_batch_id === "string" && header.legacy_batch_id.length > 0
    && count(header.revision) && header.revision >= 1 && typeof header.managed === "boolean"
    && (header.batch_date === null || date(header.batch_date)) && nullableString(header.event_name)
    && timestamp(header.created_at) && timestamp(header.updated_at)
    && nullableTimestamp(header.closed_at) && nullableTimestamp(header.deleted_at));
  // Construct the typed result explicitly; unknown RPC JSON never becomes
  // generated metadata, and extra database columns are not treated as authority.
  return {
    id: header.id, organization_id: organizationId, legacy_batch_id: header.legacy_batch_id,
    batch_date: checkedNullableString(header.batch_date), event_name: checkedNullableString(header.event_name),
    status: checkedStatus(header.status), managed: header.managed, revision: header.revision,
    created_at: checkedString(header.created_at), updated_at: checkedString(header.updated_at),
    closed_at: checkedNullableString(header.closed_at), deleted_at: checkedNullableString(header.deleted_at),
  };
}
function withAbort<T>(signal: AbortSignal, dispatch: () => PromiseLike<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(new DOMException("Saved-batch list request aborted", "AbortError"));
    };
    if (signal.aborted) { abort(); return; }
    signal.addEventListener("abort", abort, { once: true });
    try {
      // Both terminal callbacks stay attached even after cancellation, so late
      // RPC failures are consumed rather than becoming unhandled rejections.
      Promise.resolve(dispatch()).then((value) => {
        signal.removeEventListener("abort", abort);
        if (signal.aborted) abort(); else resolve(value);
      }, (error) => {
        signal.removeEventListener("abort", abort);
        if (signal.aborted) abort(); else reject(error);
      });
    } catch (error) {
      signal.removeEventListener("abort", abort);
      reject(error);
    }
  });
}

/** Caller fences dispatch using its verified actor/access context. This helper
 * only cancels consumption; it does not claim to cancel a server operation. */
export function createCoherentSavedBatchListResolver(transport: CoherentSavedBatchListTransport) {
  return async (request: { p_organization_id: string }, options: { signal: AbortSignal }): Promise<ContributionBatchHeader[]> => {
    if (options.signal.aborted) throw new DOMException("Saved-batch list request aborted", "AbortError");
    requireValid(uuid(request.p_organization_id));
    const args = { p_organization_id: request.p_organization_id };
    const raw = await withAbort(options.signal, () => data(transport.rpc("finance_list_contribution_batches", { ...args })));
    if (options.signal.aborted) throw new DOMException("Saved-batch list request aborted", "AbortError");
    requireValid(Array.isArray(raw));
    const headers = raw.map((value) => listedHeader(value, args.p_organization_id));
    requireValid(new Set(headers.map((header) => header.id)).size === headers.length);
    return headers;
  };
}

/** Requests are copied before dispatch; replay keeps the original CAS, operation,
 * raw reason and payload. A refreshed snapshot never silently rewrites a retry. */
export function createCoherentSavedBatchPort(transport: CoherentSavedBatchTransport): SavedBatchCorePort {
  return {
    async read(request) {
      const args = readContributionBatchArgs(request.p_organization_id, request.p_batch_id);
      requireValid(uuid(args.p_organization_id) && uuid(args.p_batch_id));
      return projectCoherentSavedBatch(await data(transport.rpc("finance_read_contribution_batch", structuredClone(args))), args);
    },
    async changeDate(request) {
      const args = { ...casArgs(request), p_new_date: request.p_new_date };
      requireValid(date(args.p_new_date));
      return receipt(await data(transport.rpc("finance_change_contribution_batch_date", structuredClone(args))), args, "changeDate");
    },
    async reopen(request) {
      const args = casArgs(request);
      return receipt(await data(transport.rpc("finance_reopen_contribution_batch", structuredClone(args))), args, "reopen");
    },
    async append(request: AppendContributionBatchRequest) {
      cas(request);
      requireValid(Array.isArray(request.p_entries) && request.p_entries.length > 0
        && request.p_entries.length <= 1000 && (request.p_auto_post_to_gl === undefined || typeof request.p_auto_post_to_gl === "boolean"));
      const args = appendContributionBatchArgs(structuredClone(request));
      const createdCount = request.p_entries.length;
      return receipt(await data(transport.rpc("finance_append_contribution_batch", structuredClone(args))), args, "append", createdCount);
    },
    async updateDetails(request: UpdateContributionBatchDetailsRequest) {
      cas(request);
      requireValid(nullableString(request.p_event_name));
      const args = updateContributionBatchDetailsArgs(structuredClone(request));
      return receipt(await data(transport.rpc("finance_update_contribution_batch_details", structuredClone(args))), args, "updateDetails");
    },
    async updateMember(request: UpdateContributionBatchMemberRequest): Promise<ContributionBatchMemberUpdateResult> {
      cas(request);
      requireValid(uuid(request.p_contribution_id) && request.p_complete_member_ids.includes(request.p_contribution_id)
        && typeof request.p_expected_contribution_revision === "string" && REVISION.test(request.p_expected_contribution_revision));
      const changes = object(request.p_changes);
      requireValid(Object.keys(changes).every((key) => ["person_id", "amount", "method", "check_number", "notes", "campus_id"].includes(key)));
      const args = updateContributionBatchMemberArgs(structuredClone(request));
      requireValid(Object.keys(args.p_changes).length > 0);
      const result = object(await data(transport.rpc("finance_update_contribution_batch_member", structuredClone(args))));
      const checkedReceipt = receipt(result.receipt, args, "updateMember");
      requireValid(typeof result.replayed === "boolean");
      if (result.replayed) requireValid(result.updated_record === null);
      else {
        const row = object(result.updated_record);
        requireValid(row.id === args.p_contribution_id && row.organization_id === args.p_organization_id);
      }
      return { receipt: checkedReceipt, updated_record: structuredClone(result.updated_record) as Json | null, replayed: result.replayed as boolean };
    },
  };
}
